import { USER_SUBSCRIPTION_SCHEDULED_CHANGE_TYPE, USER_SUBSCRIPTION_STATE } from '@hedepy/common/enums/SUBSCRIPTION'
import type { UserSubscriptionResponse } from '@hedepy/common/schemas/routes/api/v2/userSubscription'
import type { Dayjs } from 'dayjs'

import { dayjs } from '@/legacy-src/lib/dayjs/day'

interface ResolvedMonthUsage {
  granted: number
  used: number
  available: number
}

const bookableStates = [USER_SUBSCRIPTION_STATE.ACTIVE, USER_SUBSCRIPTION_STATE.PAUSED]

/**
 * What a calendar month grants, has spent and can still book, matching the rules the backend
 * books by (`withSubscription.ts`): a scheduled plan change owns every month from its
 * effectiveFrom — the endpoint keeps reporting the outgoing plan's grant there — a month that
 * already grants nothing keeps granting nothing, and carry-over is one pool spendable in any month.
 *
 * Null when the subscription cannot be booked against at all.
 */
export const resolveSubscriptionUsageForMonth = (
  { status, subscription, usage, scheduledChange }: UserSubscriptionResponse,
  month: Dayjs,
): ResolvedMonthUsage | null => {
  if (!subscription || !bookableStates.includes(status) || month.isBefore(dayjs(), 'month')) {
    return null
  }

  const isResolvedMonth = ({ month: reportedMonth }: { month: string }) => dayjs(reportedMonth).isSame(month, 'month')

  const monthUsage =
    usage?.currentMonth && isResolvedMonth(usage.currentMonth)
      ? usage.currentMonth
      : usage?.upcomingMonths.find(isResolvedMonth)

  const granted =
    // a zero grant is a pause, an unpaid subscription or a pending renewal — none of which the
    // incoming plan below undoes
    monthUsage?.total === 0
      ? 0
      : scheduledChange?.type === USER_SUBSCRIPTION_SCHEDULED_CHANGE_TYPE.PLAN_CHANGE &&
          !month.isBefore(dayjs(scheduledChange.effectiveFrom), 'month')
        ? scheduledChange.noOfSessionsIncluded
        : (monthUsage?.total ?? subscription.noOfSessionsIncluded)

  // a month reaches `upcomingMonths` only once it holds an event or a pause, so an absent one is
  // simply a month nothing is booked into
  const used = monthUsage?.used ?? 0

  return {
    granted,
    used,
    available: Math.max(0, Math.max(0, granted - used) + (usage?.currentMonth?.carryOver ?? 0)),
  }
}
